import React, { useState, useEffect } from 'react';
import { Activity, Clock, Zap, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import { usePerformanceMonitor, useApiPerformanceMonitor, useMemoryMonitor } from '../hooks/usePerformanceMonitor.js';

/**
 * Performance Dashboard Component
 * Shows real-time performance metrics (development only)
 */
const PerformanceDashboard = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const componentMetrics = usePerformanceMonitor('PerformanceDashboard');
  const { apiCalls, slowCalls, measureApiCall } = useApiPerformanceMonitor();
  const memoryInfo = useMemoryMonitor();

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'api', label: 'API Calls', icon: Zap },
    { id: 'memory', label: 'Memory', icon: TrendingUp }
  ];

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (ms) => {
    if (ms < 1000) return `${ms.toFixed(2)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
        title="Performance Dashboard"
      >
        <Activity className="w-5 h-5" />
      </button>

      {/* Dashboard Panel */}
      {isVisible && (
        <div className="absolute bottom-16 right-0 w-80 bg-white rounded-lg shadow-xl border max-h-96 overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Performance Dashboard</h3>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-4 max-h-64 overflow-y-auto">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium">Render Count</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {componentMetrics.renderCount}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center">
                      <Zap className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm font-medium">Avg Render Time</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {formatTime(componentMetrics.averageRenderTime)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  {componentMetrics.isSlowRender ? (
                    <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  )}
                  <span className={`text-sm ${
                    componentMetrics.isSlowRender ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {componentMetrics.isSlowRender ? 'Slow renders detected' : 'Performance is good'}
                  </span>
                </div>
              </div>
            )}

            {activeTab === 'api' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-sm font-medium text-gray-600">Total API Calls</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {apiCalls.length}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-sm font-medium text-gray-600">Slow Calls</div>
                    <div className="text-2xl font-bold text-red-600">
                      {slowCalls.length}
                    </div>
                  </div>
                </div>

                {apiCalls.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Recent Calls</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {apiCalls.slice(-5).map((call, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">{call.name}</span>
                          <span className={`font-medium ${
                            call.duration > 1000 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {formatTime(call.duration)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'memory' && memoryInfo && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-600 mb-2">Memory Usage</div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Used:</span>
                      <span className="text-sm font-medium">{formatBytes(memoryInfo.usedJSHeapSize)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Total:</span>
                      <span className="text-sm font-medium">{formatBytes(memoryInfo.totalJSHeapSize)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Limit:</span>
                      <span className="text-sm font-medium">{formatBytes(memoryInfo.jsHeapSizeLimit)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Usage</span>
                      <span>{memoryInfo.usage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          memoryInfo.usage > 80 ? 'bg-red-500' : 
                          memoryInfo.usage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${memoryInfo.usage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceDashboard;
